import React, {useEffect, useMemo, useRef, useState} from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import {QRCodeCanvas} from "qrcode.react";
import Styles from "./styles.module.scss";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import copyIcon from '../../../theme/assets/svg/tc_cab_copy_icon.svg';

import {
    cancelDirectTopUp,
    createDirectTopUp,
    createDirectTopUpFailure,
    getDashboard,
    getDirectTopUp,
    getDirectTopUpInfo,
    getUserData,
    setTopUpModalStatus
} from "../../../store/cabinet/action";
import {amountHandler, dateTimeHandler} from "../../../helpers/capsule";

// Внутренний баланс приходит без валюты — тикер берём из макета
const BALANCE_CURRENCY = '$TC';
// сервер пересканирует цепочку не чаще раза в 15 секунд на заявку
const STATUS_POLL_INTERVAL = 15000;

const timerHandler = (seconds) => {
    const left = Math.max(0, Math.floor(seconds || 0));
    const minutes = String(Math.floor(left / 60)).padStart(2, '0');
    return `${minutes}:${String(left % 60).padStart(2, '0')}`;
};

const shortAddressHandler = (address) => {
    if (!address) {
        return '';
    }
    return `${address.slice(0, 6)}…${address.slice(-4)}`;
};

const CabinetMainTopUpModal = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const topUpModalStatus = useSelector(store => store.cabinet.topUpModalStatus);
    const info = useSelector(store => store.cabinet.directTopUpInfo);
    const topUp = useSelector(store => store.cabinet.directTopUp);
    const createStarted = useSelector(store => store.cabinet.createDirectTopUpStarted);
    const formErr = useSelector(store => store.cabinet.createDirectTopUpErr);
    const infoErr = useSelector(store => store.cabinet.directTopUpInfoErr);
    const infoStarted = useSelector(store => store.cabinet.directTopUpInfoStarted);

    // amount -> payment -> status
    const [step, setStep] = useState('amount');
    const [amount, setAmount] = useState('');
    const [fromAddress, setFromAddress] = useState('');
    const [copied, setCopied] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(0);

    const copyTimerRef = useRef(false);

    // рядом с сетью показываем и токен: отправка не того токена не найдётся
    const network = useMemo(()=>{
        if (!info){
            return '';
        }
        const chain = info?.chain === 'bsc' ? 'BEP-20' : String(info?.chain || '').toUpperCase();
        const symbol = info?.token?.symbol;

        return symbol ? `${symbol} · ${chain}` : chain;
    }, [info])

    // 503 — пополнение не настроено на сервере, форму показывать нечем
    const isUnavailable = !!infoErr && !info;

    const isFulfilled = topUp?.status === 'fulfilled';
    const isExpired = topUp?.status === 'expired' || topUp?.status === 'cancelled';
    const isPending = topUp?.status === 'pending';

    useEffect(()=>{
        return ()=>{
            if (copyTimerRef.current){
                clearTimeout(copyTimerRef.current);
            }
        };
    },[])

    useEffect(()=>{
        if (topUpModalStatus){
            dispatch(getDirectTopUpInfo());
        }
    },[topUpModalStatus, dispatch])

    // обратный отсчёт до конца окна оплаты
    useEffect(()=>{
        if (!topUpModalStatus || !topUp?.expires_at || !isPending){
            return undefined;
        }

        const tick = ()=>{
            setSecondsLeft(Math.max(0, Math.floor((new Date(topUp.expires_at).getTime() - Date.now()) / 1000)));
        };

        tick();
        const timer = setInterval(tick, 1000);

        return ()=>{ clearInterval(timer); };
    },[topUpModalStatus, topUp, isPending])

    // опрос статуса заявки
    useEffect(()=>{
        const id = topUp?.id;

        if (!topUpModalStatus || !id || !isPending){
            return undefined;
        }

        const poll = setInterval(()=>{
            dispatch(getDirectTopUp(id, (res)=>{
                if (res?.status === 'fulfilled'){
                    // баланс зачислен — обновляем цифры на странице
                    dispatch(getDashboard());
                    dispatch(getUserData());
                }
            }));
        }, STATUS_POLL_INTERVAL);

        return ()=>{ clearInterval(poll); };
    },[topUpModalStatus, topUp, isPending, dispatch])

    const resetHandler = () =>{
        setStep('amount');
        setAmount('');
        setFromAddress('');
        setCopied(false);
        dispatch(createDirectTopUpFailure(false));
    }

    const closeModal = () =>{
        resetHandler();
        dispatch(setTopUpModalStatus(false));
    }

    const createHandler = () =>{
        if (isUnavailable || createStarted || !amount.trim().length || !fromAddress.trim().length){
            return;
        }

        dispatch(createDirectTopUp({
            amount: amount.trim(),
            from_address: fromAddress.trim()
        }, ()=>{ setStep('payment'); }));
    }

    // «Изменить данные» — возврат к первому шагу. Текущую заявку отменяем:
    // второй pending с того же адреса бэк не примет (422 на from_address)
    const backToAmountHandler = () =>{
        if (topUp?.id && isPending){
            dispatch(cancelDirectTopUp(topUp.id, ()=>{ setStep('amount'); }));
            return;
        }
        setStep('amount');
    }

    const copyHandler = (value) =>{
        if (!value || !navigator?.clipboard){
            return;
        }

        navigator.clipboard.writeText(value).then(()=>{
            setCopied(true);
            if (copyTimerRef.current){
                clearTimeout(copyTimerRef.current);
            }
            copyTimerRef.current = setTimeout(()=>{ setCopied(false); }, 2000);
        }).catch(()=>{});
    }

    const steps = [
        {key: 'amount', name: t('Сумма')},
        {key: 'payment', name: t('Оплата')},
        {key: 'status', name: t('Статус')}
    ];
    const stepIndex = steps.findIndex(item => item.key === step);

    const subtitle = step === 'amount'
        ? t('Укажите сумму и кошелёк, с которого отправите перевод')
        : step === 'payment'
            ? t('Отправьте перевод на адрес ниже и нажмите «Я оплатил»')
            : t('Платёж отправлен — проверяем поступление средств');

    const commonErr = formErr?.errors?.amount?.[0]
        || formErr?.errors?.from_address?.[0]
        || formErr?.message
        || '';

    // QR ведёт на адрес получателя — кошельки понимают такую строку
    const qrValue = topUp?.pay_to || info?.main_address || '';

    return (
        <Popup
            open={topUpModalStatus}
            modal
            nested
            className="capsuleModal"
            closeOnDocumentClick
            onClose={closeModal}
        >
            <div className={`${Styles.modalContainer}`}>
                <div className={`${Styles.modalCloseBtn}`} onClick={closeModal}>
                    <div/>
                    <div/>
                </div>

                <div className={`${Styles.modalContentWrap}`}>
                    <div className={`${Styles.modalHead}`}>
                        <div className={`${Styles.modalTitle}`}>{t('Пополнение баланса')}</div>
                        <div className={`${Styles.modalNote}`}>{subtitle}</div>

                        <div className={`${Styles.stepsWrap}`}>
                            {steps.map((item, index)=>(
                                <React.Fragment key={'topup-step_' + item.key}>
                                    {index ? <div className={`${Styles.stepLine} ${index <= stepIndex ? Styles.stepLineDone : ''}`}/> : null}
                                    <div className={`${Styles.stepItem} ${index === stepIndex ? Styles.stepItemActive : ''} ${index < stepIndex ? Styles.stepItemDone : ''}`}>
                                        <span className={`${Styles.stepNum}`}>{index < stepIndex ? '✓' : index + 1}</span>
                                        <span className={`${Styles.stepName}`}>{item.name}</span>
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    <div className={`${Styles.modalBody}`}>
                        {step === 'amount' && isUnavailable ? (
                            <div className={`${Styles.emptyNote}`}>
                                {infoStarted
                                    ? t('Загружаем…')
                                    : t('Пополнение баланса сейчас недоступно. Попробуйте позже.')}
                            </div>
                        ) : null}

                        {step === 'amount' && !isUnavailable ? (
                            <div className={`${Styles.formWrap}`}>
                                <div className={`${Styles.fieldBox}`}>
                                    <div className={`${Styles.fieldName}`}>{t('Сумма пополнения')}</div>
                                    <div className={`${Styles.fieldInputWrap} ${formErr?.errors?.amount ? Styles.fieldInputWrapErr : ''}`}>
                                        <input
                                            className={`${Styles.fieldInput}`}
                                            type="number"
                                            min={info?.min_amount || 1}
                                            max={info?.max_amount || undefined}
                                            step="0.01"
                                            inputMode="decimal"
                                            placeholder={info?.min_amount ? amountHandler(info.min_amount) : '1'}
                                            value={amount}
                                            onChange={(e)=>{
                                                setAmount(e.target.value);
                                                if (formErr){
                                                    dispatch(createDirectTopUpFailure(false));
                                                }
                                            }}
                                        />
                                        <span className={`${Styles.fieldChip}`}>{BALANCE_CURRENCY}</span>
                                    </div>
                                    <div className={`${Styles.fieldHints}`}>
                                        <span>{t('Минимум')} {amountHandler(info?.min_amount)} {BALANCE_CURRENCY}</span>
                                        <span>{t('Максимум')} {amountHandler(info?.max_amount)} {BALANCE_CURRENCY}</span>
                                    </div>
                                </div>

                                <div className={`${Styles.fieldBox}`}>
                                    <div className={`${Styles.fieldNameRow}`}>
                                        <span className={`${Styles.fieldName}`}>{t('Кошелёк, с которого отправляете')}</span>
                                        {network ? <span className={`${Styles.netChip}`}>{t('Сеть')} {network}</span> : null}
                                    </div>
                                    <div className={`${Styles.fieldInputWrap} ${formErr?.errors?.from_address ? Styles.fieldInputWrapErr : ''}`}>
                                        <input
                                            className={`${Styles.fieldInput}`}
                                            type="text"
                                            placeholder="0x… или T…"
                                            value={fromAddress}
                                            onChange={(e)=>{
                                                setFromAddress(e.target.value);
                                                if (formErr){
                                                    dispatch(createDirectTopUpFailure(false));
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className={`${Styles.fieldHint}`}>
                                        {t('Нужен, чтобы мы нашли ваш платёж и зачислили его на баланс')}
                                    </div>
                                </div>

                                <div className={`${Styles.summaryWrap}`}>
                                    <div className={`${Styles.summaryRow}`}>
                                        <span>{t('Срок оплаты')}</span>
                                        <span className={`${Styles.summaryValue}`}>
                                            {info?.window_minutes || 30} {t('минут после подтверждения')}
                                        </span>
                                    </div>
                                    <div className={`${Styles.summaryDivider}`}/>
                                    <div className={`${Styles.summaryRow}`}>
                                        <span>{t('Зачисление')}</span>
                                        <span className={`${Styles.summaryValue}`}>{t('обычно 5–15 минут')}</span>
                                    </div>
                                </div>

                                <div className={`${Styles.tcErr} ${commonErr ? Styles.tcErrActive : ''}`}>{commonErr}</div>
                            </div>
                        ) : null}

                        {step !== 'amount' ? (
                            <div className={`${Styles.payWrap}`}>
                                <div className={`${Styles.qrCol}`}>
                                    {qrValue ? (
                                        <div className={`${Styles.qrWrap}`}>
                                            <QRCodeCanvas value={qrValue} size={150} includeMargin/>
                                        </div>
                                    ) : null}
                                    <div className={`${Styles.qrNote}`}>{t('Отсканируйте в кошельке или скопируйте')}</div>
                                    <div className={`${Styles.addressValue}`} onClick={()=>{copyHandler(qrValue)}}>
                                        <span>{copied ? t('Скопировано') : qrValue}</span>
                                        <img src={copyIcon} alt={t('Скопировать')}/>
                                    </div>
                                    {network ? <div className={`${Styles.netChip}`}>{t('Сеть')} {network}</div> : null}
                                </div>

                                <div className={`${Styles.payCol}`}>
                                    {step === 'payment' ? (
                                        <>
                                            <div className={`${Styles.summaryWrap}`}>
                                                <div className={`${Styles.summaryRow}`}>
                                                    <span>{t('К отправке')}</span>
                                                    <span className={`${Styles.summaryValue}`}>
                                                        {amountHandler(topUp?.amount)} {BALANCE_CURRENCY}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className={`${Styles.timerWrap} ${!secondsLeft ? Styles.timerWrapOver : ''}`}>
                                                <div className={`${Styles.timerRow}`}>
                                                    <span>{t('Осталось на оплату')}</span>
                                                    <span className={`${Styles.timerValue}`}>{timerHandler(secondsLeft)}</span>
                                                </div>
                                                <div className={`${Styles.timerNote}`}>
                                                    {t('После истечения времени создайте заявку заново')}
                                                </div>
                                            </div>

                                            <div className={`${Styles.summaryWrap}`}>
                                                <div className={`${Styles.summaryRow}`}>
                                                    <span>{t('Кошелёк отправителя')}</span>
                                                    <span className={`${Styles.summaryValue}`}>{shortAddressHandler(topUp?.from_address)}</span>
                                                </div>
                                                <div className={`${Styles.summaryDivider}`}/>
                                                <div className={`${Styles.summaryRow}`}>
                                                    <span>{t('Сеть')}</span>
                                                    <span className={`${Styles.summaryValue}`}>{network}</span>
                                                </div>
                                            </div>

                                            <div className={`${Styles.ghostBtn}`} onClick={backToAmountHandler}>
                                                {t('Изменить данные')}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className={`${Styles.statusWrap} ${isFulfilled ? Styles.statusWrapDone : ''}`}>
                                                <div className={`${Styles.statusHead}`}>
                                                    <span className={`${Styles.statusMark}`}/>
                                                    <span className={`${Styles.statusPos}`}>
                                                        <span className={`${Styles.statusTitle}`}>
                                                            {isFulfilled ? t('Платёж зачислен') : isExpired ? t('Время истекло') : t('Платёж в процессе')}
                                                        </span>
                                                        <span className={`${Styles.statusNote}`}>
                                                            {isFulfilled
                                                                ? t('Средства уже на вашем балансе')
                                                                : isExpired
                                                                    ? t('Создайте заявку заново')
                                                                    : t('Проверяем поступление средств')}
                                                        </span>
                                                    </span>
                                                </div>

                                                <div className={`${Styles.checkList}`}>
                                                    <div className={`${Styles.checkRow} ${Styles.checkRowDone}`}>
                                                        <span>{t('Платёж отправлен')}</span>
                                                        <span className={`${Styles.checkMark}`}>✓</span>
                                                    </div>
                                                    <div className={`${Styles.checkRow} ${isFulfilled ? Styles.checkRowDone : Styles.checkRowProgress}`}>
                                                        <span>{t('Подтверждение платежа')}</span>
                                                        <span className={`${Styles.checkMark}`}>{isFulfilled ? '✓' : t('идёт')}</span>
                                                    </div>
                                                    <div className={`${Styles.checkRow} ${isFulfilled ? Styles.checkRowDone : ''}`}>
                                                        <span>{t('Зачисление на баланс')}</span>
                                                        <span className={`${Styles.checkMark}`}>{isFulfilled ? '✓' : t('ожидание')}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className={`${Styles.summaryWrap}`}>
                                                <div className={`${Styles.summaryRow}`}>
                                                    <span>{t('Сумма пополнения')}</span>
                                                    <span className={`${Styles.summaryValue}`}>
                                                        {amountHandler(topUp?.credited_amount || topUp?.amount)} {BALANCE_CURRENCY}
                                                    </span>
                                                </div>
                                                <div className={`${Styles.summaryDivider}`}/>
                                                <div className={`${Styles.summaryRow}`}>
                                                    <span>{t('Кошелёк отправителя')}</span>
                                                    <span className={`${Styles.summaryValue}`}>{shortAddressHandler(topUp?.from_address)}</span>
                                                </div>
                                                <div className={`${Styles.summaryDivider}`}/>
                                                <div className={`${Styles.summaryRow}`}>
                                                    <span>{t('Заявка создана')}</span>
                                                    <span className={`${Styles.summaryValue}`}>{dateTimeHandler(topUp?.created_at)}</span>
                                                </div>
                                                <div className={`${Styles.summaryDivider}`}/>
                                                <div className={`${Styles.summaryRow}`}>
                                                    <span>{t('Статус')}</span>
                                                    <span className={`${isFulfilled ? Styles.summaryValueDone : isExpired ? Styles.summaryValueErr : Styles.summaryValueProgress}`}>
                                                        {isFulfilled ? t('Завершено') : isExpired ? t('Истекло') : t('В процессе')}
                                                    </span>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        ) : null}
                    </div>

                    <div className={`${Styles.modalFooter}`}>
                        {step === 'amount' ? (
                            <>
                                <div className={`${Styles.modalBtnGhost}`} onClick={closeModal}>{t('Отмена')}</div>
                                <div
                                    className={`${Styles.modalBtn} ${isUnavailable || createStarted || !amount.trim().length || !fromAddress.trim().length ? Styles.modalBtnDsb : ''}`}
                                    onClick={createHandler}
                                >
                                    {createStarted ? t('Создаём заявку…') : `${t('Продолжить')} →`}
                                </div>
                            </>
                        ) : null}

                        {step === 'payment' ? (
                            <div
                                className={`${Styles.modalBtn} ${Styles.modalBtnWide}`}
                                onClick={()=>{setStep('status')}}
                            >
                                ✓ {t('Я оплатил')}
                            </div>
                        ) : null}

                        {step === 'status' ? (
                            <>
                                <div className={`${Styles.modalBtnGhost}`} onClick={closeModal}>{t('Закрыть')}</div>
                                <div className={`${Styles.modalBtn} ${!isFulfilled ? Styles.modalBtnDsb : ''}`} onClick={closeModal}>
                                    {isFulfilled ? `✓ ${t('Завершено')}` : `✓ ${t('Отправлено на проверку')}`}
                                </div>
                            </>
                        ) : null}
                    </div>
                </div>
            </div>
        </Popup>
    );
};

export default CabinetMainTopUpModal;
